import config from 'shared/config';
import connector from 'api/http';
import { ApiData, Excess } from 'scenes/common/types';
import {
  LandlordQuoteDetails,
  LandlordFunnelState,
  ReferenceData,
} from 'scenes/landlord/types';
import { makeApiQuoteSaved } from 'scenes/common/helpers/apiData';
import {
  marshalQuoteCreateRequest,
  unmarshalQuoteResponse,
  marshalQuoteDetailsUpdate,
  marshalQuoteUpdateOptionalExtras,
  marshalQuoteUpdateBuildingExcess,
  marshalQuoteUpdateContentsExcess,
  marshalQuoteUpdateToDataComplete,
  marshalQuoteUpdateSave,
  marshalQuoteUpdateEmail,
  marshalQuoteUpdateToPolicyDetails,
  marshalQuoteUpdatePolicyStartDate,
} from './marshaller';
import { checkPromoCode, handleError } from './services/quote';

const quoteApi = (baseUrl: string = config.baseUrls.BFF_BASE_URL) => {
  const server = connector(baseUrl);

  return {
    createQuote: (
      funnelState: LandlordFunnelState,
      referenceData: ReferenceData
    ): Promise<ApiData<LandlordQuoteDetails>> =>
      Promise.resolve(funnelState)
        .then(marshalQuoteCreateRequest)
        .then((request) => server.post('/quote/landlord/create', request))
        .then((response) => checkPromoCode(funnelState, response))
        .then(unmarshalQuoteResponse)
        .catch((err) => handleError(err, funnelState, referenceData)),

    updateQuoteDetails: (
      funnelState: LandlordFunnelState,
      referenceData: ReferenceData
    ) =>
      Promise.resolve(funnelState)
        .then(marshalQuoteDetailsUpdate)
        .then((request) => server.post('/quote/landlord/update', request))
        .then((response) => checkPromoCode(funnelState, response))
        .then(unmarshalQuoteResponse)
        .catch((err) => handleError(err, funnelState, referenceData)),

    updateOptionalExtras: (
      funnelState: LandlordFunnelState,
      selectedExtras: string[],
      referenceData: ReferenceData
    ) =>
      Promise.resolve()
        .then(() =>
          marshalQuoteUpdateOptionalExtras(funnelState, selectedExtras)
        )
        .then((request) => server.post('/quote/landlord/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) => handleError(err, funnelState, referenceData)),

    updateBuildingExcess: (
      landlordFunnelState: LandlordFunnelState,
      newExcess: Excess,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({ funnelState: landlordFunnelState, excess: newExcess })
        .then(({ funnelState, excess }) =>
          marshalQuoteUpdateBuildingExcess(funnelState, excess)
        )
        .then((request) => server.post('/quote/landlord/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) => handleError(err, landlordFunnelState, referenceData)),

    updateContentsExcess: (
      landlordFunnelState: LandlordFunnelState,
      newExcess: Excess,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({ funnelState: landlordFunnelState, excess: newExcess })
        .then(({ funnelState, excess }) =>
          marshalQuoteUpdateContentsExcess(funnelState, excess)
        )
        .then((request) => server.post('/quote/landlord/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) => handleError(err, landlordFunnelState, referenceData)),

    updateToPolicyDetails: (
      landlordFunnelState: LandlordFunnelState,
      referenceData: ReferenceData
    ) =>
      Promise.resolve(landlordFunnelState)
        .then(marshalQuoteUpdateToPolicyDetails)
        .then((request) => server.post('/quote/landlord/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) => handleError(err, landlordFunnelState, referenceData)),

    updateToDataComplete: (
      funnelState: LandlordFunnelState,
      referenceData: ReferenceData
    ) =>
      Promise.resolve(funnelState)
        .then(marshalQuoteUpdateToDataComplete)
        .then((request) => server.post('/quote/landlord/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) => handleError(err, funnelState, referenceData)),

    updateToSaveQuote: (
      landlordFunnelState: LandlordFunnelState,
      emailAddress: string,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({ funnelState: landlordFunnelState, email: emailAddress })
        .then(({ funnelState, email }) =>
          marshalQuoteUpdateSave(funnelState, email)
        )
        .then((request) => server.post('/quote/landlord/update', request))
        .then(() =>
          makeApiQuoteSaved({
            ...landlordFunnelState.apiResponses.quoteDetails.data,
            saveQuoteEmailAddress: emailAddress,
          })
        )
        .catch((err) =>
          Promise.reject(handleError(err, landlordFunnelState, referenceData))
        ),

    updateToEmailQuote: (
      landlordFunnelState: LandlordFunnelState,
      emailAddress: string,
      referenceData: ReferenceData
    ) =>
      Promise.resolve({ funnelState: landlordFunnelState, email: emailAddress })
        .then(({ funnelState, email }) =>
          marshalQuoteUpdateEmail(funnelState, email)
        )
        .then((request) => server.post('/quote/landlord/update', request))
        .then(() =>
          makeApiQuoteSaved({
            ...landlordFunnelState.apiResponses.quoteDetails.data,
            saveQuoteEmailAddress: emailAddress,
          })
        )
        .catch((err) =>
          Promise.reject(handleError(err, landlordFunnelState, referenceData))
        ),

    updatePolicyStartDate: (
      funnelState: LandlordFunnelState,
      policyStartDate: string,
      referenceData: ReferenceData
    ) => {
      const request = marshalQuoteUpdatePolicyStartDate(
        funnelState,
        policyStartDate
      );
      return Promise.resolve()
        .then(() => server.post('/quote/landlord/update', request))
        .then(unmarshalQuoteResponse)
        .catch((err) => handleError(err, funnelState, referenceData));
    },
  };
};

export default quoteApi;
